import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { DEFAULT_OVERLAYS, DEFAULT_OVERLAY_SETTINGS } from './appDefaults';
import {
  getCurrentUser,
  getPreferences,
  logout,
  savePreferences,
} from './api/auth';
import type { AiChatLastModelPreference, AuthUser, UserPreferencesPayload } from './api/auth';
import type { AiProviderId } from './api/ai';
import { normalizeChartAlertItems, normalizeChartDrawingItems } from './chartStorage';
import { AuthScreen } from './components/AuthScreen';
import { useConnectivity } from './hooks/useConnectivity';
import type {
  CandleInterval,
  ChartAlertItem,
  ChartDrawingItem,
  IndicatorKey,
  OverlaySelection,
  OverlaySettings,
  SerializedIndicatorPreferences,
} from './types';

const App = lazy(() => import('./App'));

type BootState = 'checking' | 'anonymous' | 'authenticated' | 'unavailable';

interface NormalizedPreferences {
  timeframe: CandleInterval;
  overlays: OverlaySelection;
  overlaySettings: OverlaySettings;
  indicatorPreferences: SerializedIndicatorPreferences;
  indicatorLabelsVisible?: boolean;
  showMarketStatus?: boolean;
  showIndicatorTitles?: boolean;
  mobileDrawingToolbarCollapsed?: boolean;
  alerts: ChartAlertItem[];
  drawings: ChartDrawingItem[];
  aiChatLastModel: AiChatLastModelPreference | null;
}

const INDICATOR_INSTANCES_STORAGE_KEY = '__indicatorInstances';
const SESSION_CACHE_STORAGE_KEY = 'marketproject:session-cache:v1';
const SESSION_CACHE_VERSION = 3;
const SESSION_RETRY_DELAY_MS = 5_000;

interface CachedSessionPayload {
  version: number;
  user: AuthUser;
  preferences: NormalizedPreferences;
}

const VALID_INTERVALS: CandleInterval[] = [
  '1m',
  '3m',
  '5m',
  '15m',
  '30m',
  '45m',
  '1h',
  '2h',
  '3h',
  '4h',
  '1d',
  '1w',
  '1M',
];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isValidInterval(value: unknown): value is CandleInterval {
  if (typeof value !== 'string') {
    return false;
  }
  return VALID_INTERVALS.includes(value as CandleInterval);
}

function isAiProviderId(value: unknown): value is AiProviderId {
  return value === 'openai' || value === 'gemini' || value === 'anthropic';
}

function normalizeAiChatLastModel(value: unknown): AiChatLastModelPreference | null {
  if (!isRecord(value)) {
    return null;
  }

  const provider = value.provider;
  const modelId = value.modelId;
  if (!isAiProviderId(provider) || typeof modelId !== 'string' || modelId.trim().length === 0) {
    return null;
  }

  return {
    provider,
    modelId: modelId.trim(),
  };
}

function normalizeBooleanPreference(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function normalizeOptionalBooleanPreference(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

function normalizeLegendVisibilityPreference(payload: UserPreferencesPayload): boolean | undefined {
  const indicatorLabelsVisible = normalizeOptionalBooleanPreference(payload.indicatorLabelsVisible);
  const showIndicatorTitles = normalizeOptionalBooleanPreference(payload.showIndicatorTitles);

  if (indicatorLabelsVisible === undefined && showIndicatorTitles === undefined) {
    return undefined;
  }

  return indicatorLabelsVisible === true || showIndicatorTitles === true;
}

function normalizeOverlays(value: unknown): OverlaySelection {
  if (!isRecord(value)) {
    return DEFAULT_OVERLAYS;
  }

  const normalized = { ...DEFAULT_OVERLAYS };
  for (const key of Object.keys(DEFAULT_OVERLAYS) as IndicatorKey[]) {
    const defaultValue = DEFAULT_OVERLAYS[key];
    const rawValue = value[key];
    normalized[key] = defaultValue ? rawValue !== false : rawValue === true;
  }

  return normalized;
}

function deepMergeDefaults<T>(defaults: T, candidate: unknown): T {
  if (Array.isArray(defaults)) {
    if (Array.isArray(candidate)) {
      return candidate as T;
    }
    return defaults;
  }

  if (isRecord(defaults)) {
    if (!isRecord(candidate)) {
      return defaults;
    }

    const merged: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(defaults)) {
      merged[key] = deepMergeDefaults(value, candidate[key]);
    }
    return merged as T;
  }

  if (typeof defaults === 'number') {
    return (typeof candidate === 'number' ? candidate : defaults) as T;
  }

  if (typeof defaults === 'boolean') {
    return (typeof candidate === 'boolean' ? candidate : defaults) as T;
  }

  if (typeof defaults === 'string') {
    return (typeof candidate === 'string' ? candidate : defaults) as T;
  }

  return defaults;
}

function normalizePreferences(payload: UserPreferencesPayload): NormalizedPreferences {
  const rawIndicatorPreferences = isRecord(payload.overlaySettings)
    ? payload.overlaySettings[INDICATOR_INSTANCES_STORAGE_KEY]
    : undefined;

  const overlaySettings = deepMergeDefaults(DEFAULT_OVERLAY_SETTINGS, payload.overlaySettings);
  if (isRecord(payload.overlaySettings)) {
    const hasV31Settings = isRecord(payload.overlaySettings.colorProTradingSuiteV31);
    const v3Settings = payload.overlaySettings.colorProTradingSuiteV3;
    if (!hasV31Settings && isRecord(v3Settings)) {
      overlaySettings.colorProTradingSuiteV31 = deepMergeDefaults(
        DEFAULT_OVERLAY_SETTINGS.colorProTradingSuiteV31,
        v3Settings,
      );
    }
  }

  const legendVisibility = normalizeLegendVisibilityPreference(payload);

  return {
    timeframe: isValidInterval(payload.timeframe) ? payload.timeframe : '1h',
    overlays: normalizeOverlays(payload.overlays),
    overlaySettings,
    indicatorPreferences:
      isRecord(rawIndicatorPreferences) && Array.isArray(rawIndicatorPreferences.instances)
        ? {
            instances: rawIndicatorPreferences.instances as SerializedIndicatorPreferences['instances'],
            settingsByInstance: isRecord(rawIndicatorPreferences.settingsByInstance)
              ? (rawIndicatorPreferences.settingsByInstance as SerializedIndicatorPreferences['settingsByInstance'])
              : undefined,
          }
        : { instances: [] },
    indicatorLabelsVisible: legendVisibility,
    showMarketStatus: normalizeOptionalBooleanPreference(payload.showMarketStatus),
    showIndicatorTitles: legendVisibility,
    mobileDrawingToolbarCollapsed: normalizeOptionalBooleanPreference(payload.mobileDrawingToolbarCollapsed),
    alerts: normalizeChartAlertItems(payload.alerts),
    drawings: normalizeChartDrawingItems(payload.drawings),
    aiChatLastModel: normalizeAiChatLastModel(payload.aiChatLastModel),
  };
}

function loadCachedSession(): CachedSessionPayload | null {
  try {
    const raw = window.localStorage.getItem(SESSION_CACHE_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as CachedSessionPayload;
    if (
      parsed.version !== SESSION_CACHE_VERSION ||
      !parsed.user ||
      !parsed.preferences ||
      (parsed.user.role !== 'owner' && parsed.user.role !== 'admin' && parsed.user.role !== 'user')
    ) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

function persistCachedSession(user: AuthUser, preferences: NormalizedPreferences): void {
  try {
    const payload: CachedSessionPayload = {
      version: SESSION_CACHE_VERSION,
      user,
      preferences,
    };
    window.localStorage.setItem(SESSION_CACHE_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Ignore localStorage write failures.
  }
}

function clearCachedSession(): void {
  try {
    window.localStorage.removeItem(SESSION_CACHE_STORAGE_KEY);
  } catch {
    // Ignore localStorage removal failures.
  }
}

export default function SessionApp() {
  const { isOnline } = useConnectivity();
  const [bootState, setBootState] = useState<BootState>('checking');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [initialPreferences, setInitialPreferences] = useState<NormalizedPreferences | null>(null);
  const [isUsingCachedSession, setIsUsingCachedSession] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [preferencesSaveStatus, setPreferencesSaveStatus] = useState<'saving' | 'saved' | null>(null);
  const [googleAuthRetryNonce, setGoogleAuthRetryNonce] = useState(0);
  const [sessionRetryNonce, setSessionRetryNonce] = useState(0);
  const saveStatusTimerRef = useRef<number | null>(null);
  const sessionRetryTimerRef = useRef<number | null>(null);
  const saveRequestIdRef = useRef(0);

  useEffect(() => {
    return () => {
      if (saveStatusTimerRef.current !== null) {
        window.clearTimeout(saveStatusTimerRef.current);
      }
      if (sessionRetryTimerRef.current !== null) {
        window.clearTimeout(sessionRetryTimerRef.current);
      }
    };
  }, []);

  const bootstrapSession = useCallback(async () => {
    if (sessionRetryTimerRef.current !== null) {
      window.clearTimeout(sessionRetryTimerRef.current);
      sessionRetryTimerRef.current = null;
    }

    try {
      const existingUser = await getCurrentUser();

      if (existingUser === null) {
        clearCachedSession();
        setUser(null);
        setInitialPreferences(null);
        setIsUsingCachedSession(false);
        setStatusMessage(null);
        setBootState('anonymous');
        return;
      }

      const rawPreferences = await getPreferences();
      const normalizedPreferences = normalizePreferences(rawPreferences);

      setUser(existingUser);
      setInitialPreferences(normalizedPreferences);
      setIsUsingCachedSession(false);
      setStatusMessage(null);
      setBootState('authenticated');
      persistCachedSession(existingUser, normalizedPreferences);
    } catch (error) {
      if (isRecord(error) && error.status === 401) {
        clearCachedSession();
        setUser(null);
        setInitialPreferences(null);
        setIsUsingCachedSession(false);
        setStatusMessage(null);
        setBootState('anonymous');
        return;
      }

      const cachedSession = loadCachedSession();
      const errorMessage = error instanceof Error ? error.message : 'Failed to initialize session.';

      if (isOnline) {
        sessionRetryTimerRef.current = window.setTimeout(() => {
          sessionRetryTimerRef.current = null;
          setSessionRetryNonce((value) => value + 1);
        }, SESSION_RETRY_DELAY_MS);
      }

      if (cachedSession) {
        setUser(cachedSession.user);
        setInitialPreferences(cachedSession.preferences);
        setIsUsingCachedSession(true);
        setStatusMessage(
          isOnline
            ? 'Unable to verify the live session. Retrying automatically. ' + errorMessage
            : 'Offline mode is active. Cached layout and market data will refresh when the connection returns.',
        );
        setBootState('authenticated');
        return;
      }

      setUser(null);
      setInitialPreferences(null);
      setIsUsingCachedSession(false);
      setStatusMessage(
        isOnline
          ? 'Unable to verify your session. Retrying automatically. ' + errorMessage
          : 'You are offline. Reconnect to verify your session.',
      );
      setBootState('unavailable');
    }
  }, [isOnline, sessionRetryNonce]);

  useEffect(() => {
    void bootstrapSession();
  }, [bootstrapSession]);

  const handleAuthenticated = useCallback(async (authenticatedUser: AuthUser) => {
    const rawPreferences = await getPreferences();
    const normalizedPreferences = normalizePreferences(rawPreferences);
    setUser(authenticatedUser);
    setInitialPreferences(normalizedPreferences);
    setIsUsingCachedSession(false);
    setStatusMessage(null);
    setBootState('authenticated');
    persistCachedSession(authenticatedUser, normalizedPreferences);
  }, []);

  const handleLogout = useCallback(async () => {
    setIsLoggingOut(true);
    try {
      await logout();
      clearCachedSession();
      setUser(null);
      setInitialPreferences(null);
      setIsUsingCachedSession(false);
      setStatusMessage(null);
      setBootState('anonymous');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to sign out.';
      setStatusMessage(message);
    } finally {
      setIsLoggingOut(false);
    }
  }, []);

  const handlePreferencesChange = useCallback(
    async (
      payload: UserPreferencesPayload,
      options: { keepalive?: boolean } = {},
    ) => {
      const keepalive = options.keepalive === true;
      const requestId = saveRequestIdRef.current + 1;
      saveRequestIdRef.current = requestId;

      if (!keepalive) {
        if (saveStatusTimerRef.current !== null) {
          window.clearTimeout(saveStatusTimerRef.current);
          saveStatusTimerRef.current = null;
        }
        setPreferencesSaveStatus('saving');
      }

      try {
        await savePreferences(payload, { keepalive });
        if (user) {
          persistCachedSession(user, normalizePreferences(payload));
        }
        if (!keepalive && requestId === saveRequestIdRef.current) {
          setStatusMessage(null);
          setPreferencesSaveStatus('saved');
          saveStatusTimerRef.current = window.setTimeout(() => {
            if (requestId === saveRequestIdRef.current) {
              setPreferencesSaveStatus(null);
            }
          }, 1200);
        }
      } catch (error) {
        if (!keepalive && requestId === saveRequestIdRef.current) {
          setPreferencesSaveStatus(null);
          const message = error instanceof Error ? error.message : 'Failed to save preferences.';
          setStatusMessage(message);
        }
      }
    },
    [user],
  );

  const handleSessionExpired = useCallback(() => {
    clearCachedSession();
    setUser(null);
    setInitialPreferences(null);
    setIsUsingCachedSession(false);
    setBootState('anonymous');
    setStatusMessage('Session expired. Please sign in again.');
  }, []);

  useEffect(() => {
    if (!isOnline && bootState === 'authenticated' && !isUsingCachedSession) {
      setStatusMessage('Offline mode is active. Cached market data stays visible until the connection returns.');
    }

  }, [bootState, isOnline, isUsingCachedSession]);

  if (bootState === 'checking') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950 px-4 py-8 text-slate-200">
        Checking session...
      </div>
    );
  }

  if (bootState === 'unavailable') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950 px-4 py-8 text-slate-200">
        <div className="w-full max-w-md rounded-lg border border-amber-400/40 bg-slate-900/80 p-6 text-center">
          <h1 className="text-lg font-semibold">Unable to verify your session</h1>
          <p className="mt-2 text-sm text-slate-300">{statusMessage}</p>
          <button
            type="button"
            onClick={() => setSessionRetryNonce((value) => value + 1)}
            className="mt-4 rounded-md border border-amber-300/60 px-4 py-2 text-sm font-medium text-amber-100 transition hover:bg-amber-400/10"
          >
            Retry session
          </button>
        </div>
      </div>
    );
  }

  if (bootState === 'anonymous' || user === null || initialPreferences === null) {
    return (
      <>
        <AuthScreen onAuthenticated={handleAuthenticated} retryNonce={googleAuthRetryNonce} />
        {statusMessage ? (
          <div className="fixed bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-3 rounded-full border border-rose-500/50 bg-rose-500/10 px-4 py-2 text-sm text-rose-200">
            <span>{statusMessage}</span>
            <button
              type="button"
              onClick={() => setGoogleAuthRetryNonce((value) => value + 1)}
              className="rounded-full border border-rose-300/60 px-3 py-1 text-xs font-medium text-rose-100 transition hover:bg-rose-400/20"
            >
              Retry login
            </button>
          </div>
        ) : null}
      </>
    );
  }

  return (
    <>
      <Suspense
        fallback={
          <div className="flex min-h-screen items-center justify-center bg-slate-950 px-4 py-8 text-slate-200">
            Loading workspace...
          </div>
        }
      >
        <App
          key={user.id}
          initialTimeframe={initialPreferences.timeframe}
          initialOverlays={initialPreferences.overlays}
          initialOverlaySettings={initialPreferences.overlaySettings}
          initialIndicatorPreferences={initialPreferences.indicatorPreferences}
          initialIndicatorLabelsVisible={initialPreferences.indicatorLabelsVisible}
          initialShowMarketStatus={initialPreferences.showMarketStatus}
          initialShowIndicatorTitles={initialPreferences.showIndicatorTitles}
          initialMobileDrawingToolbarCollapsed={initialPreferences.mobileDrawingToolbarCollapsed}
          initialAlerts={initialPreferences.alerts}
          initialDrawings={initialPreferences.drawings}
          initialAiChatLastModel={initialPreferences.aiChatLastModel}
          username={user.username}
          userRole={user.role}
          onLogout={isUsingCachedSession ? undefined : handleLogout}
          isLoggingOut={isLoggingOut}
          onSessionExpired={handleSessionExpired}
          onPreferencesChange={isUsingCachedSession ? undefined : handlePreferencesChange}
        />
      </Suspense>
      {statusMessage ? (
        <p
          className={`pointer-events-none fixed bottom-4 left-1/2 -translate-x-1/2 rounded-full border px-4 py-2 text-sm ${
            isUsingCachedSession || !isOnline
              ? 'border-amber-400/50 bg-amber-500/10 text-amber-100'
              : 'border-rose-500/50 bg-rose-500/10 text-rose-200'
          }`}
        >
          {statusMessage}
        </p>
      ) : null}
      {preferencesSaveStatus ? (
        <p
          className={`pointer-events-none fixed bottom-4 right-4 rounded-full border px-4 py-2 text-xs font-medium ${
            preferencesSaveStatus === 'saving'
              ? 'border-amber-400/60 bg-amber-500/10 text-amber-200'
              : 'border-emerald-400/60 bg-emerald-500/10 text-emerald-200'
          }`}
        >
          {preferencesSaveStatus === 'saving' ? 'Saving...' : 'Saved'}
        </p>
      ) : null}
    </>
  );
}
